import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { startVersionWatch } from "./lib/versionCheck";
import LoginPage from "./pages/LoginPage";
import ExternalSetupPage from "./pages/ExternalSetupPage";
import ExternalUsersPage from "./pages/ExternalUsersPage";
import SsoAcceptPage from "./pages/SsoAcceptPage";
import DashboardLayout from "./pages/DashboardLayout";
import SkyworthUpload from "./pages/SkyworthUpload";
import CaptureScreen from "./pages/CaptureScreen";
import ThirdPartyDashboard from "./pages/ThirdPartyDashboard";
import StaffDashboard from "./pages/StaffDashboard";
import JobDetail from "./pages/JobDetail";
import ManagementDashboard from "./pages/ManagementDashboard";
import ApprovalsPage from "./pages/ApprovalsPage";
import JobList from "./pages/JobList";
import UserManagement from "./pages/UserManagement";
import SystemConfig from "./pages/SystemConfig";
import NotificationsPage from "./pages/NotificationsPage";
import ArchivePage from "./pages/ArchivePage";
import AccountingBoard from "./pages/AccountingBoard";
import CMAccountsBoard from "./pages/CMAccountsBoard";
import ThirdPartyJobsBoard from "./pages/ThirdPartyJobsBoard";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-teal-600 font-heading text-lg animate-pulse">          Loading CargoDash...
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" />;
  return children;
}

/**
 * Intercepts `?sso_token=...` on ANY route and forwards to /sso/accept
 * BEFORE the login form (or any other page) has a chance to mount.
 * This is the spec's "home route must inspect for ?sso_token=" requirement.
 */
function SsoTokenInterceptor({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("sso_token");
    if (token && location.pathname !== "/sso/accept") {
      navigate(`/sso/accept?sso_token=${encodeURIComponent(token)}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return children;
}

function RoleBasedHome() {
  const { user } = useAuth();
  const routes = { SS: "/upload", SM: "/dashboard", CI: "/capture", CS: "/staff", CM: "/dashboard", "3PA": "/agent", EXTERNAL: "/jobs", SA: "/accounting" };
  return <Navigate to={routes[user?.role] || "/jobs"} />;
}

function JobsRoute() {
  const { user } = useAuth();
  // CS now also operates the local-delivery pipeline end-to-end, so they get
  // the same drag-and-drop kanban as 3PA / EXTERNAL agents on /jobs.
  if (["3PA", "EXTERNAL", "CS"].includes(user?.role)) return <ThirdPartyJobsBoard />;
  return <JobList />;
}

function App() {
  useEffect(() => {
    // Build-version watcher: polls /api/version and force-reloads with a
    // `?v=<stamp>` cache-buster the moment the backend redeploys. This
    // is what makes new releases visible to users immediately — both in
    // a normal browser tab AND inside the BlackScott Hub iframe — even
    // when Cloudflare is still serving a stale `index.html` from edge.
    const stop = startVersionWatch();
    return stop;
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <SsoTokenInterceptor>
          <ErrorBoundary>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/external/setup" element={<ExternalSetupPage />} />
            <Route path="/sso/accept" element={<SsoAcceptPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<RoleBasedHome />} />
              <Route path="upload" element={<SkyworthUpload />} />
              <Route path="capture" element={<CaptureScreen />} />
              <Route path="capture/:jobId" element={<CaptureScreen />} />
              <Route path="agent" element={<ThirdPartyDashboard />} />
              <Route path="staff" element={<StaffDashboard />} />
              <Route path="jobs" element={<JobsRoute />} />
              <Route path="jobs/:jobId" element={<JobDetail />} />
              <Route path="local-deliveries" element={<ThirdPartyJobsBoard />} />
              <Route path="dashboard" element={<ManagementDashboard />} />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="external-users" element={<ExternalUsersPage />} />
              <Route path="config" element={<SystemConfig />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="archive" element={<ArchivePage />} />
              <Route path="accounting" element={<AccountingBoard />} />
              <Route path="accounting/archive" element={<AccountingBoard archived={true} />} />
              <Route path="accounts" element={<CMAccountsBoard />} />
              <Route path="accounts/archive" element={<CMAccountsBoard archived={true} />} />
            </Route>
          </Routes>
          </ErrorBoundary>
        </SsoTokenInterceptor>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
